import config from "../config/index.js";
import { useChainTime } from "../hooks/useChainTime.js";
import { DEPOSIT_STATE } from "../hooks/useDeposits.js";
import { formatUsdcCompact, liveAccruedInterest, displayAprLabel } from "../utils/format.js";

/**
 * Detailed payout preview shown inside the Early Exit confirmation
 * modal. Walks the user through every component of what they're
 * forfeiting and what they'll actually receive:
 *
 *   Principal Returned  +$X    (principal - penalty)
 *   Interest            +$Y    (accrued so far)
 *   Loan                -$Z    (outstanding, must be repaid)
 *   ─────────────────────────
 *   You'll Receive $W   = (Principal - Penalty + Interest) - Loan
 *
 * Mirrors the on-chain payout from earlyExit(): the user receives
 * `principal - penalty + interest`, with any outstanding loan repaid
 * from that amount. The early-exit penalty (Standard 20%, Turbo 50%) is
 * shown separately as a warning since it's the cost of exiting early.
 * The SafeVault slice (80% of principal) is only the funding source/cap,
 * NOT the amount returned — for Turbo the penalty is 50% so the user
 * receives 50% of principal + interest, not 80%.
 */
export default function EarlyExitBreakdown({ deposit }) {
    // Hook MUST be called before the early return below (Rules of
    // Hooks). Cheap when `deposit` is null since the consumer of
    // `chainNowSec` never runs.
    const { chainNowSec } = useChainTime();
    if (!deposit) return null;

    const principal = BigInt(deposit.principal || 0);
    // Replay the contract's day-stepped accrual formula instead of
    // reading the stored value — `deposit.accruedInterest` only moves
    // on chain events, so it reads 0 for an Active deposit until the
    // exit itself fires. The user needs to see what they'll ACTUALLY
    // get back before confirming; the contract computes the same
    // amount inside earlyExit() at execution time.
    // §V6.2 — per-deposit rate.
    const interest = Number(deposit.state) === DEPOSIT_STATE.Active
        ? liveAccruedInterest(
              deposit.principal,
              deposit.startTime,
              deposit.maturityTime,
              chainNowSec,
              Number(deposit.annualRateBps) || config.protocol.annualRateBps,
              deposit.realizedInterest
          )
        : BigInt(deposit.accruedInterest || 0);
    const loan = BigInt(deposit.loanBalance || 0);

    // §V6.2 — per-deposit penalty (Turbo 50%, Standard 20%). The SafeVault
    // routing (80% of principal) is only a funding source/cap — it is NOT
    // the amount returned to the user. Match the contract's RoundingLib
    // (floor in protocol's favour).
    const isTurboProduct = Number(deposit.productMode) === 1;
    const penaltyBps = BigInt(
        Number(deposit.earlyExitPenaltyBps) || (isTurboProduct ? 5000 : 2000)
    );
    const penalty = (principal * penaltyBps) / 10_000n;
    const penaltyPct = Number(penaltyBps) / 100;

    // §V6.2 — mirror the on-chain payout (GilderSystem.earlyExit): the user
    // receives `principal - penalty`, so the returned principal is 80% for
    // Standard (penalty 20%) and 50% for Turbo (penalty 50%).
    const returnedPrincipal = principal > penalty ? principal - penalty : 0n;
    const returnedPrincipalPct = (10_000 - Number(penaltyBps)) / 100;

    // Display-only APR label (Turbo shows "up to 27.5%", accrues 20%). The
    // interest math above uses the real stored rate; this is just the label.
    const aprPct = displayAprLabel(deposit);

    // §V6.2 — payout = principal − penalty + interest (GilderSystem.sol:1109),
    // with any outstanding loan repaid from that amount.
    const grossReceive = returnedPrincipal + interest;
    const netReceive = grossReceive > loan ? grossReceive - loan : 0n;

    return (
        <div>
            <p className="text-[12px] leading-relaxed text-white/85">
                Closing Term Deposit #{deposit.id} triggers a{" "}
                <span className="font-semibold text-[#e6c985]">
                    {penaltyPct}% early-exit penalty
                </span>{" "}
                on the original principal. The penalty is routed to the Distribution Reserve
                and is non-refundable. This cannot be undone.
            </p>

            <ul className="mt-3 space-y-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 p-3 text-[12px]">
                <BreakdownRow
                    label="Principal Returned"
                    sub={`${returnedPrincipalPct}% of principal (after penalty)`}
                    value={`+$${formatUsdcCompact(returnedPrincipal)}`}
                    valueClass="text-white"
                />
                <BreakdownRow
                    label="Interest Earned"
                    sub="Accrued so far"
                    value={`+$${formatUsdcCompact(interest)}`}
                    valueClass="text-white"
                />
                {loan > 0n ? (
                    <BreakdownRow
                        label="Outstanding Loan"
                        sub="Repaid from payout"
                        value={`-$${formatUsdcCompact(loan)}`}
                        valueClass="text-[#ff8a8a]"
                    />
                ) : null}
                <li className="flex items-center justify-between gap-3 border-t border-white/15 pt-2">
                    <span className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        You&rsquo;ll Receive
                    </span>
                    <span className="font-manrope text-[15px] font-bold text-white">
                        ${formatUsdcCompact(netReceive)}
                    </span>
                </li>
            </ul>

            <p className="mt-3 text-[10.5px] leading-snug text-[#e6c985]/70">
                Penalty:{" "}
                <span className="font-semibold text-[#ff8a8a]">
                    -${formatUsdcCompact(penalty)}
                </span>{" "}
                forfeited to the Distribution Reserve ({penaltyPct}% of principal). Compared to settling at
                maturity you give up the remaining {aprPct}% APR for the unused term.
            </p>
        </div>
    );
}

function BreakdownRow({ label, sub, value, valueClass = "" }) {
    return (
        <li className="flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[12px] font-semibold text-white">{label}</div>
                <div className="text-[10px] text-[#e6c985]/70">{sub}</div>
            </div>
            <span className={`font-manrope text-[13px] font-semibold ${valueClass}`}>
                {value}
            </span>
        </li>
    );
}
